/**
 * Deposit-Protection helpers — EIP-7710 redeemable delegations
 * targeted at the Takeover protocol's seat-deposit invariants.
 *
 * Surface (the takeover-app consumes all of these):
 *   - `DELEGATION_TYPED_DATA` — EIP-712 typed-data primary type +
 *     domain builder (matches MDF v1.x's `Delegation` schema).
 *   - `buildDelegation(...)` — build a typed-data object the user
 *     signs once when toggling Deposit Protection ON.
 *   - `encodeDelegationCaveats(...)` — compose the four caveat
 *     tuples (deposit-protection, boardroom-keeper, ERC20 weekly
 *     allowance, expiry timestamp).
 *   - `buildRedeemDelegationsCall(...)` — encode the call to MDF
 *     `DelegationManager.redeemDelegations`.
 *   - `buildProtectSeatRedemption(...)` — high-level helper that
 *     bundles the keeper-side calldata for one top-up. The keeper
 *     submits the result of this directly to the DM.
 *
 * Why mirror MDF here instead of importing the upstream package?
 *   The takeover-app already vendors its SDK (`packages/sdk`) so the
 *   build stays self-contained; pulling in MDF's full TS package
 *   would balloon the bundle and rebind us to its release cadence.
 *   The schema we mirror is small (~60 LOC) and stable as of v1.3.x.
 */

import {
  type Abi,
  type Address,
  type Hex,
  decodeAbiParameters,
  encodeAbiParameters,
  encodeFunctionData,
  hashStruct,
  hashTypedData,
  parseAbi,
  toFunctionSelector,
} from 'viem';
import type { AbiCall } from './types';

// ─── MDF protocol-level sentinels ─────────────────────────────────

/**
 * MDF v1.3.0's "any delegate" sentinel. When `delegation.delegate ==
 * ANY_DELEGATE`, the onchain `DelegationManager` skips the
 * `delegate == msg.sender` check and lets any address call
 * `redeemDelegations` against the delegation. We use this so any
 * boardroom-seat keeper (gated by `BoardroomKeeperEnforcer`) can
 * execute a top-up, not just a single pre-baked relayer.
 *
 * Source: `DelegationManager.sol#L44` —
 *   `address public constant ANY_DELEGATE = address(0xa11);`
 *
 * NOT to be confused with `ROOT_AUTHORITY` (the bytes32 sentinel for
 * leaf delegations) — different field, different type.
 */
export const ANY_DELEGATE: Address = '0x0000000000000000000000000000000000000a11';

/**
 * MDF v1.3.0's "self-rooted" authority sentinel. When the leaf
 * delegation's `authority == ROOT_AUTHORITY`, the DM accepts the
 * delegator as the root authority directly (vs. chained delegations
 * where `authority` points at the parent's hash). We always use this
 * — Deposit Protection is a single-hop user→keeper authorization, no
 * intermediaries.
 *
 * Source: `DelegationManager.sol#L41` —
 *   `bytes32 public constant ROOT_AUTHORITY =`
 *   `0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff;`
 */
export const ROOT_AUTHORITY: Hex =
  '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff';

// ─── EIP-712 typed-data ───────────────────────────────────────────

/**
 * MDF `Delegation` typed-data primary type. Every field except
 * `signature` is hashed; the signature itself is the recovered EIP-712
 * digest. The schema is identical to MDF v1.3.x — verbatim.
 */
export const DELEGATION_TYPED_DATA = {
  Delegation: [
    { name: 'delegate', type: 'address' },
    { name: 'delegator', type: 'address' },
    { name: 'authority', type: 'bytes32' },
    { name: 'caveats', type: 'Caveat[]' },
    { name: 'salt', type: 'uint256' },
  ],
  Caveat: [
    { name: 'enforcer', type: 'address' },
    { name: 'terms', type: 'bytes' },
  ],
} as const;

/**
 * Domain separator the user's wallet hashes the delegation against.
 * Salt is intentionally left out — MDF uses a versioned name + chain
 * id only, so two deployments of the same DM on the same chain (a
 * known anti-pattern, but possible) collide. We accept that since
 * we point at one canonical DM per chain.
 */
export function delegationDomain(
  chainId: number,
  delegationManager: Address,
): { name: string; version: string; chainId: number; verifyingContract: Address } {
  return {
    name: 'DelegationManager',
    version: '1',
    chainId,
    verifyingContract: delegationManager,
  };
}

/**
 * The full EIP-712 DIGEST of a delegation — `keccak("\x19\x01" ‖
 * domainSeparator ‖ structHash)`, i.e. the exact value the user's
 * wallet signs.
 *
 * ⚠️ This is NOT what `IDelegationManager.getDelegationHash` returns,
 * and NOT the value the DM emits in `RedeemedDelegation` /
 * `DisabledDelegation` or that caveat enforcers key their state by —
 * those all use the domain-less STRUCT hash. Use
 * `computeDelegationStructHash` for anything that must join against
 * onchain identifiers. (Storing this digest as `delegation_hash`
 * silently broke event reconciliation for every 7715 row — the
 * grid-8 seat-108 incident.)
 *
 * Note we exclude `signature` from the typed-data message — same as
 * the onchain hashing — so the hash is stable regardless of which
 * keeper / wallet eventually relays the signed delegation.
 */
export function computeDelegationHash(
  chainId: number,
  delegationManager: Address,
  delegation: Pick<DelegationFields, 'delegate' | 'delegator' | 'authority' | 'caveats' | 'salt'>,
): Hex {
  return hashTypedData({
    domain: delegationDomain(chainId, delegationManager),
    types: DELEGATION_TYPED_DATA,
    primaryType: 'Delegation',
    message: {
      delegate: delegation.delegate,
      delegator: delegation.delegator,
      authority: delegation.authority,
      caveats: delegation.caveats.map((c) => ({ enforcer: c.enforcer, terms: c.terms })),
      salt: delegation.salt,
    },
  });
}

/**
 * TRUE mirror of `IDelegationManager.getDelegationHash` — the
 * domain-less EIP-712 STRUCT hash of the delegation. This is the
 * delegation's onchain identity: the DM emits it in
 * `RedeemedDelegation` / `DisabledDelegation` events and every caveat
 * enforcer (`ERC20PeriodTransferEnforcer` etc.) keys its per-delegation
 * state by it. Persist THIS as `delegation_hash` so reconciliation can
 * join indexed events back to DB rows.
 *
 * Verified against Base mainnet: for the grid-8 seat-108 grant this
 * returns `0x8f62475c…` (matching `DelegationManager.getDelegationHash`
 * and the period enforcer's storage key), where `computeDelegationHash`
 * returns the signed digest `0xc7d7cf39…`.
 */
export function computeDelegationStructHash(
  delegation: Pick<DelegationFields, 'delegate' | 'delegator' | 'authority' | 'caveats' | 'salt'>,
): Hex {
  return hashStruct({
    types: DELEGATION_TYPED_DATA,
    primaryType: 'Delegation',
    data: {
      delegate: delegation.delegate,
      delegator: delegation.delegator,
      authority: delegation.authority,
      caveats: delegation.caveats.map((c) => ({ enforcer: c.enforcer, terms: c.terms })),
      salt: delegation.salt,
    },
  });
}

// ─── Caveat shapes ────────────────────────────────────────────────

/**
 * One caveat in the user-signed delegation. `args` is intentionally
 * left out of the signature: MDF treats it as per-redemption data the
 * keeper provides, NOT part of the signed authorization. This is the
 * mechanism we use to inject the keeper's address + per-call seat
 * params into the enforcers without re-prompting the user.
 */
export interface Caveat {
  enforcer: Address;
  terms: Hex;
  /** Filled by the keeper at redemption time. Empty when signing. */
  args: Hex;
}

export interface DelegationFields {
  delegate: Address;
  delegator: Address;
  authority: Hex;
  caveats: Caveat[];
  salt: bigint;
  /** Filled after the user signs. */
  signature: Hex;
}

// ─── Caveat encoders ──────────────────────────────────────────────

/**
 * Protocol-wide bounds on the user-signed runway gate / target. Mirror
 * the onchain `MIN_GATE_S` / `MAX_GATE_S` / `MIN_TARGET_S` /
 * `MAX_TARGET_S` constants in `DepositProtectionEnforcer.sol`. We
 * also bounds-check client-side so a misconfigured frontend fails
 * loudly before consuming a signature prompt.
 */
export const PROTECTION_BOUNDS = {
  minGateSeconds: 60 * 60, // 1 hour
  maxGateSeconds: 14 * 24 * 60 * 60, // 14 days
  minTargetSeconds: 60 * 60, // 1 hour
  maxTargetSeconds: 30 * 24 * 60 * 60, // 30 days
} as const;

export interface DepositProtectionTermsParams {
  allowedUgms: Address[];
  /** Top-up trigger threshold, in seconds (e.g. 12h, 72h). */
  runwayGateSeconds: number;
  /** Cap on runway-after-top-up, in seconds (e.g. 1d, 7d). */
  targetRunwaySeconds: number;
}

/**
 * `DepositProtectionEnforcer.terms` is
 * `abi.encode(address[] allowedUgms, uint256 gateS, uint256 targetS)`.
 *
 *   - `allowedUgms` pins the UGMs the keeper may target; widening it
 *     requires a fresh delegation signature, by design.
 *   - `gateS` is the runway threshold below which a top-up is permitted.
 *   - `targetS` is the maximum runway *after* a top-up.
 *
 * Both values must lie within `PROTECTION_BOUNDS` and `targetS` must
 * exceed `gateS` — same checks the onchain enforcer performs at
 * decode time.
 */
export function encodeDepositProtectionTerms(p: DepositProtectionTermsParams): Hex {
  if (p.allowedUgms.length === 0) {
    throw new Error('encodeDepositProtectionTerms: allowedUgms must not be empty');
  }
  if (
    p.runwayGateSeconds < PROTECTION_BOUNDS.minGateSeconds ||
    p.runwayGateSeconds > PROTECTION_BOUNDS.maxGateSeconds
  ) {
    throw new Error(
      `runwayGateSeconds out of bounds [${PROTECTION_BOUNDS.minGateSeconds}, ${PROTECTION_BOUNDS.maxGateSeconds}]`,
    );
  }
  if (
    p.targetRunwaySeconds < PROTECTION_BOUNDS.minTargetSeconds ||
    p.targetRunwaySeconds > PROTECTION_BOUNDS.maxTargetSeconds
  ) {
    throw new Error(
      `targetRunwaySeconds out of bounds [${PROTECTION_BOUNDS.minTargetSeconds}, ${PROTECTION_BOUNDS.maxTargetSeconds}]`,
    );
  }
  if (p.targetRunwaySeconds <= p.runwayGateSeconds) {
    throw new Error('targetRunwaySeconds must exceed runwayGateSeconds');
  }
  return encodeAbiParameters(
    [{ type: 'address[]' }, { type: 'uint256' }, { type: 'uint256' }],
    [p.allowedUgms, BigInt(p.runwayGateSeconds), BigInt(p.targetRunwaySeconds)],
  );
}

/**
 * `ERC20PeriodicAllowanceEnforcer.terms` encodes a (token, period,
 * amount) tuple. The encoding here matches MDF v1.3.x.
 *
 * `period` is in seconds. We default to 1 week (the user-visible
 * "weekly cap" period).
 */
export interface PeriodicAllowanceParams {
  token: Address;
  amount: bigint;
  /** Defaults to 7 days. */
  periodSeconds?: number;
  /** Anchor for the rolling-window math. Defaults to "now". */
  startTimestamp?: number;
}

export function encodeWeeklyAllowanceTerms(p: PeriodicAllowanceParams): Hex {
  const period = BigInt(p.periodSeconds ?? 7 * 86400);
  const start = BigInt(p.startTimestamp ?? Math.floor(Date.now() / 1000));
  return encodeAbiParameters(
    [
      { type: 'address' }, // token
      { type: 'uint256' }, // amount
      { type: 'uint256' }, // period
      { type: 'uint256' }, // startTimestamp
    ],
    [p.token, p.amount, period, start],
  );
}

/**
 * `TimestampEnforcer.terms` = `abi.encode(uint128 after, uint128 before)`.
 * For "valid until 90 days from now" we set `after = 0` and
 * `before = expiresAt`. MDF reads `block.timestamp >= after` and
 * `block.timestamp <= before` so both inclusive bounds.
 */
export function encodeTimestampTerms(params: {
  validAfter?: number;
  validBefore: number;
}): Hex {
  return encodeAbiParameters(
    [{ type: 'uint128' }, { type: 'uint128' }],
    [BigInt(params.validAfter ?? 0), BigInt(params.validBefore)],
  );
}

// ─── Per-redemption args ──────────────────────────────────────────

/**
 * Args the keeper fills into the `DepositProtectionEnforcer` caveat
 * at redemption time. Mirror of the onchain decoder
 * (`abi.decode(args, (address, uint256, uint256, uint256, address))`).
 *
 * The previous 6-field layout carried a per-keeper `keeperClaim` so
 * the enforcer could cross-check that the claimed fee recipient
 * matched the onchain `_redeemer`. Now that the fee is pinned to the
 * `BoardroomKeeperFeeAdapter` immutable, that field is gone — anyone
 * may submit a redemption and every fee flows to the same address.
 */
export interface DepositProtectionArgs {
  ugm: Address;
  gridId: bigint;
  seatId: bigint;
  principal: bigint;
  taxToken: Address;
}

export function encodeDepositProtectionArgs(a: DepositProtectionArgs): Hex {
  return encodeAbiParameters(
    [
      { type: 'address' },
      { type: 'uint256' },
      { type: 'uint256' },
      { type: 'uint256' },
      { type: 'address' },
    ],
    [a.ugm, a.gridId, a.seatId, a.principal, a.taxToken],
  );
}

// ─── Top-level delegation builder ─────────────────────────────────

export interface BuildDelegationParams {
  delegate: Address;
  delegator: Address;
  /** Compose with caveat encoders above. */
  caveats: Caveat[];
  /** A nonce for delegation rotation. Default `Date.now()` ms. */
  salt?: bigint;
  /**
   * Parent delegation hash for chained delegations. Defaults to
   * `ROOT_AUTHORITY` — the onchain DM rejects leaf delegations
   * with any other value (including `bytes32(0)`). Override only
   * when stitching a delegation chain (POC doesn't, by design).
   */
  authority?: Hex;
}

/**
 * Build the typed-data object the user signs. The result feeds
 * directly into `wallet_signTypedData_v4`. The returned object
 * INCLUDES `args` for symmetry with the EIP-712 `Caveat` type, but
 * the EIP-712 schema we register only hashes `enforcer + terms` —
 * `args` is a per-redemption payload by spec.
 */
export function buildDelegation(
  chainId: number,
  delegationManager: Address,
  params: BuildDelegationParams,
): {
  domain: ReturnType<typeof delegationDomain>;
  types: typeof DELEGATION_TYPED_DATA;
  primaryType: 'Delegation';
  message: {
    delegate: Address;
    delegator: Address;
    authority: Hex;
    salt: bigint;
    caveats: Array<{ enforcer: Address; terms: Hex }>;
  };
} {
  const salt = params.salt ?? BigInt(Date.now());
  return {
    domain: delegationDomain(chainId, delegationManager),
    types: DELEGATION_TYPED_DATA,
    primaryType: 'Delegation',
    message: {
      delegate: params.delegate,
      delegator: params.delegator,
      // Default to `ROOT_AUTHORITY` — anything else (including
      // `bytes32(0)`) trips MDF's `InvalidAuthority` revert on the
      // leaf delegation, since `delegations_[length-1].authority !=
      // ROOT_AUTHORITY` is a hard fail in `redeemDelegations`.
      authority: params.authority ?? ROOT_AUTHORITY,
      salt,
      // Strip `args` from the signed payload — the user is signing
      // the immutable terms only.
      caveats: params.caveats.map((c) => ({ enforcer: c.enforcer, terms: c.terms })),
    },
  };
}

// ─── DelegationManager.redeemDelegations encoder ──────────────────

/**
 * Minimal ABI for `DelegationManager.redeemDelegations(...)`. We do
 * NOT import the full DM ABI — the rest of the surface (single
 * delegations, signature overrides, batch redemptions, disable) is
 * out of scope for the POC.
 */
export const DELEGATION_MANAGER_ABI = parseAbi([
  'function redeemDelegations(bytes[] permissionContexts, bytes32[] modes, bytes[] executionCallDatas)',
]) as Abi;

/**
 * ERC-7579 batch mode code: `[0x01, 0x00, ...zero]`. The first byte
 * is `callType=BATCH`. We pin it as a constant so wallets and the
 * enforcer agree on the mode bytes.
 */
export const BATCH_MODE_CODE: Hex =
  '0x0100000000000000000000000000000000000000000000000000000000000000';

/**
 * ERC-7579 single mode code: all-zero. First byte `callType=SINGLE`.
 * Pinned as a constant for the v2 ProtectionRedeemer rail — MetaMask's
 * `ERC20PeriodTransferEnforcer` is `onlySingleCallTypeMode` so the
 * 7715 path can only redeem in SINGLE mode.
 */
export const SINGLE_MODE_CODE: Hex =
  '0x0000000000000000000000000000000000000000000000000000000000000000';

/** ERC-7579 execution element. */
export interface ExecutionStruct {
  target: Address;
  value: bigint;
  callData: Hex;
}

const EXECUTION_TUPLE = {
  type: 'tuple[]',
  components: [
    { name: 'target', type: 'address' },
    { name: 'value', type: 'uint256' },
    { name: 'callData', type: 'bytes' },
  ],
} as const;

const DELEGATION_TUPLE = {
  type: 'tuple[]',
  components: [
    { name: 'delegate', type: 'address' },
    { name: 'delegator', type: 'address' },
    { name: 'authority', type: 'bytes32' },
    {
      name: 'caveats',
      type: 'tuple[]',
      components: [
        { name: 'enforcer', type: 'address' },
        { name: 'terms', type: 'bytes' },
        { name: 'args', type: 'bytes' },
      ],
    },
    { name: 'salt', type: 'uint256' },
    { name: 'signature', type: 'bytes' },
  ],
} as const;

/**
 * `DelegationManager.disableDelegation(Delegation)` — the delegator
 * permanently disables a delegation it signed, so subsequent
 * `redeemDelegations` of that hash revert. The single-struct param
 * reuses the canonical `DELEGATION_TUPLE` component layout so the
 * calldata encoding matches the contract's struct exactly. The DM
 * computes the same hash `computeDelegationHash` produces and only
 * checks `msg.sender == delegation.delegator`.
 */
export const DISABLE_DELEGATION_ABI = [
  {
    type: 'function',
    name: 'disableDelegation',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'delegation', type: 'tuple', components: DELEGATION_TUPLE.components }],
    outputs: [],
  },
] as unknown as Abi;

/** Shape `DelegationFields` into the onchain `Delegation` struct
 *  (`args` defaulted to `0x`, as it is for a signed leaf delegation). */
export function toDelegationStruct(d: DelegationFields): {
  delegate: Address;
  delegator: Address;
  authority: Hex;
  caveats: Array<{ enforcer: Address; terms: Hex; args: Hex }>;
  salt: bigint;
  signature: Hex;
} {
  return {
    delegate: d.delegate,
    delegator: d.delegator,
    authority: d.authority,
    caveats: d.caveats.map((c) => ({ enforcer: c.enforcer, terms: c.terms, args: c.args ?? '0x' })),
    salt: d.salt,
    signature: d.signature,
  };
}

export function encodeExecutionBatch(calls: ExecutionStruct[]): Hex {
  return encodeAbiParameters([EXECUTION_TUPLE], [calls]);
}

export function encodeDelegationChain(chain: DelegationFields[]): Hex {
  return encodeAbiParameters([DELEGATION_TUPLE], [chain]);
}

/**
 * Inverse of `encodeDelegationChain` — decodes a `permissionContext`
 * blob (the bytes the keeper feeds into
 * `DelegationManager.redeemDelegations`) back into the typed
 * delegation chain.
 *
 * Used by the server-side `/api/protection/enable` route to verify
 * MM-emitted ERC-7715 grants, since those arrive as opaque
 * `permissionContext` hex rather than as the user-signed typed-data
 * struct. A buggy / malicious wallet that emits a malformed blob will
 * trip viem's strict ABI decoder here and surface as a clean
 * `Error` rather than a corrupt persist.
 */
export function decodeDelegationChain(permissionContext: Hex): DelegationFields[] {
  const [decoded] = decodeAbiParameters([DELEGATION_TUPLE], permissionContext) as [
    Array<{
      delegate: Address;
      delegator: Address;
      authority: Hex;
      caveats: ReadonlyArray<{ enforcer: Address; terms: Hex; args: Hex }>;
      salt: bigint;
      signature: Hex;
    }>,
  ];
  return decoded.map((d) => ({
    delegate: d.delegate,
    delegator: d.delegator,
    authority: d.authority,
    caveats: d.caveats.map((c) => ({ enforcer: c.enforcer, terms: c.terms, args: c.args })),
    salt: d.salt,
    signature: d.signature,
  }));
}

// ─── Top-up redemption builder ────────────────────────────────────

const ERC20_APPROVE_SEL = toFunctionSelector('function approve(address,uint256)');
const ERC20_TRANSFER_SEL = toFunctionSelector('function transfer(address,uint256)');
const UGM_ADD_DEPOSIT_SEL = toFunctionSelector('function addDeposit(uint256,uint256,uint256)');

export interface BuildTopUpRedemptionParams {
  delegation: DelegationFields;
  delegationManager: Address;
  ugm: Address;
  gridId: bigint;
  seatId: bigint;
  /** Top-up amount in tax-token base units. Excludes fee. */
  principal: bigint;
  taxToken: Address;
  /** Address of `DepositProtectionEnforcer` — used to find which
   *  caveat to set `args` on. */
  depositProtectionEnforcer: Address;
  /** `BoardroomKeeperFeeAdapter` address — fees flow here on every
   *  redemption. Pinned to the same immutable on the onchain
   *  enforcer; mismatch will revert with `WrongCallArgs(2)`. */
  feeAdapter: Address;
}

/**
 * Pre-computed, ABI-encoded calldata for one top-up redemption.
 *
 * Returned as raw arrays rather than a `redeemDelegations` call so
 * the caller can submit it through one of two rails:
 *
 *   - `DelegationManager.redeemDelegations(...)` — the standard MDF
 *     rail; useful if the boardroom has 0 USDC fees pending and the
 *     caller just wants to top up one seat.
 *   - `BoardroomKeeperFeeAdapter.redeemAndFlush(...)` — preferred
 *     rail; bundles N redemptions and pushes the resulting USDC to
 *     the boardroom in a single tx. The "Collect Fees" UI uses this.
 *
 * `buildProtectSeatRedemptionCall` and `buildKeeperBatchSweepCall`
 * below wrap each rail in an `AbiCall`.
 */
export interface ProtectionRedemptionPlan {
  permissionContext: Hex;
  mode: Hex;
  executionCallData: Hex;
  fee: bigint;
}

/**
 * Build a single redemption's `(permissionContext, mode,
 * executionCallData)` triple. Fee is fixed to 1% of principal.
 *
 * Pure / no I/O.
 */
export function planProtectSeatRedemption(
  p: BuildTopUpRedemptionParams,
): ProtectionRedemptionPlan {
  const fee = (p.principal * 100n) / 10000n; // 1%
  if (fee === 0n) {
    throw new Error('planProtectSeatRedemption: principal too small — fee rounds to 0');
  }

  // Inject per-redemption args into the matching caveat. Other
  // caveats (timestamp, periodic allowance, …) pass through verbatim
  // — they don't read `args`.
  const mutatedCaveats: Caveat[] = p.delegation.caveats.map((c) => {
    if (c.enforcer.toLowerCase() === p.depositProtectionEnforcer.toLowerCase()) {
      return {
        enforcer: c.enforcer,
        terms: c.terms,
        args: encodeDepositProtectionArgs({
          ugm: p.ugm,
          gridId: p.gridId,
          seatId: p.seatId,
          principal: p.principal,
          taxToken: p.taxToken,
        }),
      };
    }
    return c;
  });

  const mutatedDelegation: DelegationFields = {
    ...p.delegation,
    caveats: mutatedCaveats,
  };

  // ERC-20 approve(ugm, principal)
  const approveData = (ERC20_APPROVE_SEL +
    encodeAbiParameters(
      [{ type: 'address' }, { type: 'uint256' }],
      [p.ugm, p.principal],
    ).slice(2)) as Hex;
  // UGM.addDeposit(gridId, seatId, principal)
  const addDepositData = (UGM_ADD_DEPOSIT_SEL +
    encodeAbiParameters(
      [{ type: 'uint256' }, { type: 'uint256' }, { type: 'uint256' }],
      [p.gridId, p.seatId, p.principal],
    ).slice(2)) as Hex;
  // ERC-20 transfer(feeAdapter, fee) — fee always sinks to the
  // pinned adapter, never to the per-tx submitter.
  const transferData = (ERC20_TRANSFER_SEL +
    encodeAbiParameters(
      [{ type: 'address' }, { type: 'uint256' }],
      [p.feeAdapter, fee],
    ).slice(2)) as Hex;

  const exec: ExecutionStruct[] = [
    { target: p.taxToken, value: 0n, callData: approveData },
    { target: p.ugm, value: 0n, callData: addDepositData },
    { target: p.taxToken, value: 0n, callData: transferData },
  ];

  return {
    permissionContext: encodeDelegationChain([mutatedDelegation]),
    mode: BATCH_MODE_CODE,
    executionCallData: encodeExecutionBatch(exec),
    fee,
  };
}

/**
 * Wrap a single redemption plan as an `AbiCall` against the
 * `DelegationManager` directly. Most callers prefer the batch sweep
 * helper below, which routes through the fee adapter to flush USDC
 * in the same tx.
 */
export function buildProtectSeatRedemption(p: BuildTopUpRedemptionParams): {
  call: AbiCall;
  fee: bigint;
} {
  const plan = planProtectSeatRedemption(p);
  return {
    call: {
      to: p.delegationManager,
      abi: DELEGATION_MANAGER_ABI,
      functionName: 'redeemDelegations',
      args: [[plan.permissionContext], [plan.mode], [plan.executionCallData]],
    },
    fee: plan.fee,
  };
}

// ─── Boardroom keeper-fee adapter helpers ─────────────────────────

/**
 * Minimal ABI for `BoardroomKeeperFeeAdapter`. Only the surface the
 * lobby UI ("Collect Fees" button) and the cron / SDK need — the
 * owner-only sweeps live in the contract but don't need a TS helper
 * since the Boardroom guardian uses Etherscan / Foundry for those
 * (rare, manual operations).
 */
export const BOARDROOM_KEEPER_FEE_ADAPTER_ABI = parseAbi([
  'function redeemAndFlush(bytes[] permissionContexts, bytes32[] modes, bytes[] executionCallDatas)',
  'function flushUsdc()',
  'function pendingYield(bytes32) view returns (uint256)',
  'event UsdcFlushed(uint256 amount, address indexed caller)',
  'event NonUsdcClaimed(address indexed token, address indexed to, uint256 amount)',
  'event BatchRedeemed(uint256 n, address indexed caller)',
]) as Abi;

/**
 * Bundle N redemption plans into a single `redeemAndFlush` call on
 * the boardroom keeper-fee adapter. Each redemption's fee lands in
 * the adapter; `redeemAndFlush` then pushes the accumulated USDC to
 * the boardroom grid as yield, in the same tx.
 *
 * Use this instead of N separate `DM.redeemDelegations` calls when:
 *   - The lobby UI's "Collect Fees" button needs one user
 *     confirmation regardless of how many seats are protected.
 *   - The cron wants atomic redemption + flush so a partial flush
 *     can't leave fees stranded between blocks.
 */
export function buildKeeperBatchSweep(params: {
  feeAdapter: Address;
  redemptions: ProtectionRedemptionPlan[];
}): { call: AbiCall; totalFee: bigint } {
  if (params.redemptions.length === 0) {
    throw new Error('buildKeeperBatchSweep: redemptions must not be empty');
  }
  const permissionContexts = params.redemptions.map((r) => r.permissionContext);
  const modes = params.redemptions.map((r) => r.mode);
  const executionCallDatas = params.redemptions.map((r) => r.executionCallData);
  const totalFee = params.redemptions.reduce((acc, r) => acc + r.fee, 0n);
  return {
    call: {
      to: params.feeAdapter,
      abi: BOARDROOM_KEEPER_FEE_ADAPTER_ABI,
      functionName: 'redeemAndFlush',
      args: [permissionContexts, modes, executionCallDatas],
    },
    totalFee,
  };
}

/**
 * Encode a `redeemAndFlush(...)` call as raw calldata. Used by
 * server-side keepers that submit txs via `viem.walletClient`
 * directly rather than through `AbiCall` plumbing.
 */
export function encodeRedeemAndFlushCall(plans: ProtectionRedemptionPlan[]): Hex {
  return encodeFunctionData({
    abi: BOARDROOM_KEEPER_FEE_ADAPTER_ABI,
    functionName: 'redeemAndFlush',
    args: [
      plans.map((p) => p.permissionContext),
      plans.map((p) => p.mode),
      plans.map((p) => p.executionCallData),
    ],
  });
}

// ─── ProtectionRedeemer (V2) helpers — ERC-7715 / MetaMask rail ───

/**
 * Why v2: MetaMask's `wallet_requestExecutionPermissions` attaches an
 * `ERC20PeriodTransferEnforcer` caveat that is `onlySingleCallTypeMode`
 * and only permits `transfer(...)` calls on the granted token. The v1
 * redeemer's 3-call BATCH (approve + addDeposit + transfer-fee) hits
 * the very first caveat with `CaveatEnforcer:invalid-call-type` and
 * reverts.
 *
 * v2 flips the flow:
 *   1. The user's smart account performs ONE call per redemption:
 *      `transfer(redeemer, principal+fee)` on the seat's tax token.
 *      That satisfies the MM caveat exactly.
 *   2. After the DM returns, the redeemer holds `principal+fee` per
 *      row and composes the `approve` + `addDeposit` + `transfer-fee`
 *      steps from its own balance. None of those calls go through
 *      the user's delegation.
 *
 * On-chain validation: same invariants as the 7710 path (seat owner,
 * UGM allowlist, runway gate / target) — but they now run on a
 * caller-supplied per-row tuple alongside the DM redemption.
 *
 * Existing v1 grants are unusable: the DM's `delegate == msg.sender`
 * check pins them to the v1 redeemer address. Users must revoke and
 * re-grant against the v2 redeemer (frontend dialog drives this).
 */

/** Per-row tuple the redeemer cross-checks against the DM call. */
export interface ProtectionRedemptionItem {
  ugm: Address;
  gridId: bigint;
  seatId: bigint;
  taxToken: Address;
  principal: bigint;
  fee: bigint;
}

export const PROTECTION_REDEEMER_ABI = parseAbi([
  'function redeem((address ugm,uint256 gridId,uint256 seatId,address taxToken,uint256 principal,uint256 fee)[] items, bytes[] permissionContexts, bytes32[] modes, bytes[] executionCallDatas)',
  'function allowedUgms() view returns (address[])',
  'function isAllowedUgm(address) view returns (bool)',
  'function GATE_S() view returns (uint256)',
  'function TARGET_S() view returns (uint256)',
  'function FEE_ADAPTER() view returns (address)',
  'function DELEGATION_MANAGER() view returns (address)',
  'event BatchRedeemed(uint256 n, address indexed caller)',
]) as Abi;

const ITEMS_TUPLE = {
  type: 'tuple[]',
  components: [
    { name: 'ugm', type: 'address' },
    { name: 'gridId', type: 'uint256' },
    { name: 'seatId', type: 'uint256' },
    { name: 'taxToken', type: 'address' },
    { name: 'principal', type: 'uint256' },
    { name: 'fee', type: 'uint256' },
  ],
} as const;

/**
 * ERC-7579 SINGLE-mode `executionCallData` encoder.
 *
 * The single-mode encoding is **packed** (not abi-encoded):
 *   `target (20 bytes) ++ value (32 bytes) ++ callData (rest)`
 *
 * This matches `ExecutionLib.encodeSingle(...)` in the MDF / ERC-7579
 * reference implementation. Wallets and our v2 redeemer pin the same
 * shape on both sides — emitting it the abi-encoded way (target +
 * value + bytes) would silently surface as `WrongCallArgs` because
 * the redeemer's length check uses the packed layout's exact size.
 */
function encodeSingleExecutionCallData(call: ExecutionStruct): Hex {
  const targetHex = call.target.slice(2).toLowerCase().padStart(40, '0');
  const valueHex = call.value.toString(16).padStart(64, '0');
  const dataHex = call.callData.startsWith('0x') ? call.callData.slice(2) : call.callData;
  return `0x${targetHex}${valueHex}${dataHex}` as Hex;
}

export interface BuildTopUpRedemptionV2Params {
  delegation: DelegationFields;
  redeemer: Address;
  ugm: Address;
  gridId: bigint;
  seatId: bigint;
  /** Top-up amount in tax-token base units. Excludes fee. */
  principal: bigint;
  taxToken: Address;
}

/** Plan-result for v2: items[] tuple paired with the DM redemption triple. */
export interface ProtectionRedemptionPlanV2 {
  item: ProtectionRedemptionItem;
  permissionContext: Hex;
  mode: Hex;
  executionCallData: Hex;
  fee: bigint;
}

/**
 * Build the v2 `(item, permissionContext, mode, executionCallData)`
 * tuple for one redemption.
 *
 * The DM call is a SINGLE-mode `transfer(redeemer, principal+fee)` on
 * the tax token. The per-row item captures the seat metadata the
 * redeemer needs to compose the onchain side (approve, addDeposit,
 * transfer-fee) from its own balance after the DM pulls the funds.
 *
 * Fee is fixed to 1% of principal (matches `ProtectionLib.FEE_BPS`).
 * Pure / no I/O.
 */
export function planProtectSeatRedemptionV2(
  p: BuildTopUpRedemptionV2Params,
): ProtectionRedemptionPlanV2 {
  const fee = (p.principal * 100n) / 10000n;
  if (fee === 0n) {
    throw new Error('planProtectSeatRedemptionV2: principal too small — fee rounds to 0');
  }

  // The MM caveat strips per-redemption `args`, so we don't need to
  // inject anything into caveat slots like the v1 path did — the v2
  // redeemer validates the per-row tuple against the SINGLE call.
  const transferData = (ERC20_TRANSFER_SEL +
    encodeAbiParameters(
      [{ type: 'address' }, { type: 'uint256' }],
      [p.redeemer, p.principal + fee],
    ).slice(2)) as Hex;

  const executionCallData = encodeSingleExecutionCallData({
    target: p.taxToken,
    value: 0n,
    callData: transferData,
  });

  return {
    item: {
      ugm: p.ugm,
      gridId: p.gridId,
      seatId: p.seatId,
      taxToken: p.taxToken,
      principal: p.principal,
      fee,
    },
    permissionContext: encodeDelegationChain([p.delegation]),
    mode: SINGLE_MODE_CODE,
    executionCallData,
    fee,
  };
}

/**
 * Bundle N v2 redemption plans into one `ProtectionRedeemerV2.redeem`
 * call. Use this for any delegation whose `signing_path` is
 * `'7715-grant'` in the DB. For `'7710-direct'` rows, use
 * `buildKeeperBatchSweep` (which targets the BKFA's `redeemAndFlush`).
 *
 * Mixing both rails in the same onchain tx is unsupported — the
 * caller must split by `signing_path` before encoding. The lobby's
 * "Collect Fees" hook does this split before issuing the txs.
 */
export function buildRedeemerBatch(params: {
  redeemer: Address;
  redemptions: ProtectionRedemptionPlanV2[];
}): { call: AbiCall; totalFee: bigint } {
  if (params.redemptions.length === 0) {
    throw new Error('buildRedeemerBatch: redemptions must not be empty');
  }
  const items = params.redemptions.map((r) => r.item);
  const permissionContexts = params.redemptions.map((r) => r.permissionContext);
  const modes = params.redemptions.map((r) => r.mode);
  const executionCallDatas = params.redemptions.map((r) => r.executionCallData);
  const totalFee = params.redemptions.reduce((acc, r) => acc + r.fee, 0n);
  return {
    call: {
      to: params.redeemer,
      abi: PROTECTION_REDEEMER_ABI,
      functionName: 'redeem',
      args: [items, permissionContexts, modes, executionCallDatas],
    },
    totalFee,
  };
}

/**
 * Encode a `ProtectionRedeemerV2.redeem(...)` call as raw calldata.
 * Used by server-side keepers (or scripted tools) that submit via
 * `viem.walletClient` directly. Mirrors `encodeRedeemAndFlushCall`.
 */
export function encodeProtectionRedeemCall(plans: ProtectionRedemptionPlanV2[]): Hex {
  return encodeFunctionData({
    abi: PROTECTION_REDEEMER_ABI,
    functionName: 'redeem',
    args: [
      plans.map((p) => p.item),
      plans.map((p) => p.permissionContext),
      plans.map((p) => p.mode),
      plans.map((p) => p.executionCallData),
    ],
  });
}

/**
 * Encode the items tuple alone — handy when callers want to inspect
 * the onchain layout without committing to a full `redeem` call.
 */
export function encodeProtectionRedemptionItems(items: ProtectionRedemptionItem[]): Hex {
  return encodeAbiParameters([ITEMS_TUPLE], [items]);
}

// ─── High-level "configure delegation" helper ─────────────────────

export interface BuildProtectionDelegationParams {
  chainId: number;
  delegationManager: Address;
  delegator: Address;
  /**
   * Defaults to `ANY_DELEGATE = address(0xa11)` — MDF's open-
   * delegation sentinel — so any boardroom-seat keeper can call
   * `redeemDelegations`. The keeper-set `BoardroomKeeperEnforcer`
   * caveat enforces seat ownership at redemption time.
   *
   * If the caller pins this to a specific address, only that EOA
   * can ever redeem — the POC must NOT do this, since the cron's
   * keeper rotates and the protection model assumes a permissionless
   * pool.
   */
  delegate?: Address;
  enforcers: {
    depositProtection: Address;
    erc20PeriodicAllowance: Address;
    timestamp: Address;
  };
  config: {
    allowedUgms: Address[];
    weeklyUsdcCap: bigint;
    weeklyTakeoverCap: bigint;
    usdcAddress: Address;
    takeoverAddress: Address;
    expiresAt: number;
    /** Top-up trigger threshold, in seconds. */
    runwayGateSeconds: number;
    /** Cap on runway-after-top-up, in seconds. */
    targetRunwaySeconds: number;
  };
  salt?: bigint;
}

/**
 * Compose the standard caveats and return a typed-data object ready
 * for `wallet_signTypedData_v4`. Caller submits the signed payload +
 * caveat list to `/api/protection/enable`.
 *
 * Caveat order (must match `/api/protection/enable` validation):
 *   1. `DepositProtectionEnforcer` — runway gate, target, UGM allowlist
 *   2. `ERC20PeriodicAllowanceEnforcer` (USDC) — weekly cap, optional
 *   3. `ERC20PeriodicAllowanceEnforcer` (TAKEOVER) — weekly cap, optional
 *   4. `TimestampEnforcer` — global expiry
 *
 * The `BoardroomKeeperEnforcer` was retired in v2: fees flow to the
 * pinned `BoardroomKeeperFeeAdapter` regardless of who submits, so
 * the per-keeper boardroom-seat gate is no longer needed. Anyone may
 * relay a redemption.
 */
export function buildProtectionDelegation(p: BuildProtectionDelegationParams): {
  typedData: ReturnType<typeof buildDelegation>;
  caveats: Caveat[];
} {
  const caveats: Caveat[] = [];

  caveats.push({
    enforcer: p.enforcers.depositProtection,
    terms: encodeDepositProtectionTerms({
      allowedUgms: p.config.allowedUgms,
      runwayGateSeconds: p.config.runwayGateSeconds,
      targetRunwaySeconds: p.config.targetRunwaySeconds,
    }),
    args: '0x',
  });

  if (p.config.weeklyUsdcCap > 0n) {
    caveats.push({
      enforcer: p.enforcers.erc20PeriodicAllowance,
      terms: encodeWeeklyAllowanceTerms({
        token: p.config.usdcAddress,
        amount: p.config.weeklyUsdcCap,
      }),
      args: '0x',
    });
  }
  if (p.config.weeklyTakeoverCap > 0n) {
    caveats.push({
      enforcer: p.enforcers.erc20PeriodicAllowance,
      terms: encodeWeeklyAllowanceTerms({
        token: p.config.takeoverAddress,
        amount: p.config.weeklyTakeoverCap,
      }),
      args: '0x',
    });
  }

  caveats.push({
    enforcer: p.enforcers.timestamp,
    terms: encodeTimestampTerms({ validBefore: p.config.expiresAt }),
    args: '0x',
  });

  const typedData = buildDelegation(p.chainId, p.delegationManager, {
    // Default to MDF's "any delegate" sentinel — anyone can redeem.
    delegate: p.delegate ?? ANY_DELEGATE,
    delegator: p.delegator,
    caveats,
    salt: p.salt,
  });

  return { typedData, caveats };
}
